/**
 * 위·아래 두 칸과 그 사이를 끄는 막대 — 편집기/결과 분할(2026-10-10, react-split-pane 을 대신한다).
 *
 * 바꾼 까닭: 웹의 화면 확대는 html 에 CSS zoom 을 건다(utils/appZoom). react-split-pane 은 마우스 좌표(화면 px)와
 * 칸 크기를 섞어 계산해서, 확대 중에 막대를 100px 끌자 칸이 5,600px 이 됐다(실측) — 라이브러리 안쪽이라 고칠 수 없다.
 * 여기서는 끈 거리와 잰 크기를 모두 CSS px 로 바꿔(`toCssPx`) 계산한다.
 *
 * 마크업·클래스는 react-split-pane 과 같다(.SplitPane .Pane1 .Resizer.horizontal .Pane2) — App.css 의 막대 모양이 그대로 든다.
 * size: 위 칸 높이(px 숫자 | '100%' 같은 CSS 값 | undefined → defaultSize). 끄는 동안 onChange(px) 를 부른다.
 */
import React, { useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toCssPx } from '../../utils/appZoom';

const ROOT_STYLE = {
  display: 'flex', flexDirection: 'column', flex: 1, position: 'absolute', top: 0, bottom: 0, left: 0, right: 0,
  height: '100%', width: '100%', overflow: 'hidden', outline: 'none',
};

function VerticalSplit({ size, defaultSize = '50%', onChange, minSize = 50, pane1Style, pane2Style, resizerStyle, children }) {
  const rootRef = useRef(null);
  const pane1Ref = useRef(null);
  const drag = useRef(null);
  const [live, setLive] = useState(null);   // 끄는 동안의 높이(px) — 부모가 size 를 늦게 줘도 막대가 손을 따라온다
  const [top, bottom] = React.Children.toArray(children);

  const onPointerDown = useCallback((e) => {
    if (e.button !== 0 || !rootRef.current || !pane1Ref.current) return;
    const root = rootRef.current.getBoundingClientRect();
    const p1 = pane1Ref.current.getBoundingClientRect();
    // 모두 CSS px 로 — getBoundingClientRect·clientY 는 화면 px 다(웹의 화면 확대 중에는 다르다)
    drag.current = { y0: e.clientY, h0: toCssPx(p1.height), max: toCssPx(root.height) - minSize };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_e) { /* 오래된 브라우저 */ }
    e.preventDefault();   // 끄는 동안 글자가 선택되지 않게
  }, [minSize]);

  const onPointerMove = useCallback((e) => {
    const d = drag.current;
    if (!d) return;
    const h = Math.round(Math.max(minSize, Math.min(d.max, d.h0 + toCssPx(e.clientY - d.y0))));
    setLive(h);
    if (onChange) onChange(h);
  }, [minSize, onChange]);

  const onPointerUp = useCallback((e) => {
    if (!drag.current) return;
    drag.current = null;
    setLive(null);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_e) { /* 이미 풀림 */ }
  }, []);

  const h = live != null ? live : (size != null ? size : defaultSize);
  return (
    <div ref={rootRef} className="SplitPane horizontal" style={ROOT_STYLE}>
      <div ref={pane1Ref} className="Pane horizontal Pane1" style={{ flex: '0 0 auto', position: 'relative', outline: 'none', height: h, ...pane1Style }}>
        {top}
      </div>
      <span
        className="Resizer horizontal"
        role="separator"
        aria-orientation="horizontal"
        style={resizerStyle}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      />
      <div className="Pane horizontal Pane2" style={{ flex: 1, position: 'relative', outline: 'none', ...pane2Style }}>
        {bottom}
      </div>
    </div>
  );
}

VerticalSplit.propTypes = {
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  defaultSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
  minSize: PropTypes.number,
  pane1Style: PropTypes.object,
  pane2Style: PropTypes.object,
  resizerStyle: PropTypes.object,
  children: PropTypes.node,
};

export default VerticalSplit;
